<template lang="pug">
.bulk-audio.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-customisations"
        )
          | Bulk Customisations
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-translation-jobs"
        )
          | Bulk Translation Jobs
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible
    .max-w-screen-2xl.mx-auto
      .prose.dark_prose-invert.max-w-none
        h1.font-normal 
          span {{ pageTitle }}

        .mb-6
          label.block.text-sm.font-medium.text-gray-700.dark_text-gray-300.mb-2
            | Workflow
          select.w-full.max-w-md.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
            v-model="bulkAudioMode"
          )
            option(value="tier2") Audio Generation (Tier-2 S3, full-course)
            option(value="jit-cleanup") Remove Legacy S3 Audio Paths (For JIT)

        template(v-if="bulkAudioMode === 'tier2'")
          p 
            span This tool will re-run AI Audio for all customisations with existing audio. It can also add new audio languages to customisations that don't currently have audio by specifying firm codes and language codes (see warning below). It creates audio scripts, generates audio files and updates the audio path in the YAML. It then publishes the courses to the internal or live environment.
          p.text-sm.text-purple-700.dark_text-purple-300
            | Languages with 
            code course.audio-ai-jit
            |  enabled are skipped automatically — this tool only regenerates Tier-2 (pre-recorded S3) audio.
          p.font-bold.text-orange-600.dark_text-orange-400
            span Before you proceed you must ensure that:
            ul
              li base courses have been published LIVE
              li custom translations have been published LIVE
          
          p.font-bold.text-red-600.dark_text-red-400(v-if="hasAnyUnsavedChanges")
            span ⚠️ Some selected courses have unsaved changes. Please save or commit all changes before creating audio scripts.

        BulkAudioJitCleanup(v-else)

        .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg(v-if="bulkAudioMode === 'tier2'")
          h2.p-0.mt-0 Course Selection
          
          p Enter base course ID(s) to find all customisations. You can also specify firm code and language codes to add audio properties to customisations that don't currently have audio.
          
          .grid.grid-cols-1.gap-4
            .p-4.border.border-gray-300.dark_border-gray-600.rounded-lg
              .flex.gap-4.items-end
                .flex-1
                  label.block.text-sm.font-medium.text-gray-700.dark_text-gray-300.mb-2
                    | Base course IDs (comma separated)
                  input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
                    type="text"
                    placeholder="e.g. 1000, 1001, 1002"
                    v-model="courseIdsInput"
                    :disabled="loadingCustomisations"
                    @keyup.enter="loadCustomisations"
                  )
            
            .p-4.border.border-orange-300.dark_border-orange-600.rounded-lg.cursor-pointer(
              @click="toggleNewAudioLanguagesSection"
            )
              .flex.items-center.justify-between.mb-4
                h4.text-gray-900.dark_text-gray-100.mt-0.mb-0
                  | Add NEW audio languages?
                .text-orange-600.dark_text-orange-400.text-lg
                  span(v-if="isNewAudioLanguagesExpanded") ▲
                  span(v-else) ▼
              
              .transition-all.duration-300.ease-in-out(
                :class="{ 'max-h-0 overflow-hidden opacity-0': !isNewAudioLanguagesExpanded, 'max-h-screen opacity-100': isNewAudioLanguagesExpanded }"
              )
                p.font-bold.text-orange-600.dark_text-orange-400.mb-4
                  span ℹ️ Use carefully! All translations must exist and be published LIVE before adding NEW audio languages.
                
                p.text-sm.text-gray-600.dark_text-gray-400.mb-4
                  | Specify firm code and language codes to <strong>add NEW audio</strong> properties to customisations that <strong>don't currently have audio</strong>. Use the 📋 button to automatically populate language codes from all selected customisations.
                
                .grid.grid-cols-1.md_grid-cols-2.gap-4
                  .flex-1
                    label.block.text-sm.font-medium.text-gray-700.dark_text-gray-300.mb-2
                      | Firm Code (optional)
                    input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
                      type="text"
                      placeholder="e.g. customfirm123"
                      v-model="firmCodeInput"
                      :disabled="loadingCustomisations"
                      @click.stop
                    )
                  
                  .flex-1
                    label.block.text-sm.font-medium.text-gray-700.dark_text-gray-300.mb-2
                      | Language Codes (comma separated)
                    .flex.gap-2
                      input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
                        type="text"
                        placeholder="e.g. es, fr, de"
                        v-model="languageCodesInput"
                        :disabled="loadingCustomisations"
                        @click.stop
                      )
                      button.btn-hover_cursor-pointer.bg-blue-600.btn-hover_bg-blue-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-3.rounded(
                        type="button"
                        :disabled="loadingCustomisations || selectedCoursesCount === 0"
                        @click.stop="populateLanguageCodesFromCustomisations"
                        title="Populate with ALL languages from the selected customisations"
                      )
                        span.text-sm 📋
                    .text-xs.mt-1(v-if="languageCodesInput && languageCodesInput.trim()")
                      .text-red-600.dark_text-red-400(v-if="languageValidationErrors && languageValidationErrors.length > 0")
                        .div(v-for="error in languageValidationErrors" :key="error") {{ error }}
                      .text-green-600.dark_text-green-400(v-else-if="parseLanguageCodes(languageCodesInput) && parseLanguageCodes(languageCodesInput).length > 0")
                        | ✓ {{ parseLanguageCodes(languageCodesInput).length }} valid language code(s)
              
            .p-4.border.border-gray-300.dark_border-gray-600.rounded-lg
              .flex.gap-4.items-end
                button.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                  type="button"
                  :disabled="!courseIdsInput.trim() || loadingCustomisations || isCreatingScripts || isCreatingAudio || isPublishing"
                  @click="loadCustomisations"
                )
                  span(v-if="!loadingCustomisations") Find Customisations
                  span(v-else) Loading...
                button.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-gray-600.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                  type="button"
                  :disabled="isCreatingScripts || isCreatingAudio || isPublishing"
                  @click="resetSelection"
                ) 
                  span Reset
              
              .loading-container(v-if="loadingCustomisations")
                p.text-lg.font-semibold.mb-2
                  | Loading customisations...

              .summary-container(v-else-if="customisationsReports.size > 0 && !loadingCustomisations")
                p.text-lg.font-semibold(
                  v-if="totalSummary.totalCustomisations > 0"
                )
                  | Found {{ totalSummary.totalBaseCourses }} base course(s) with {{ totalSummary.totalCustomisations }} total customisations
                  span
                    |  ({{ totalSummary.totalWithAudio }} with audio)
                
                  //- generate and download a report of the customisations
                  span.ms-4
                    button.btn-hover_cursor-pointer.bg-blue-600.btn-hover_bg-blue-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-1.px-4.rounded(
                      type="button"
                      :disabled="customisationsReports.size === 0"
                      @click="downloadCustomisationsReport"
                    )
                      span Download Customisations Report
                
                p.text-sm.text-gray-600.dark_text-gray-400(v-if="checkingUnsavedChanges")
                  | Checking for unsaved changes...
                p.text-sm.text-red-600.dark_text-red-400(v-else-if="hasAnyUnsavedChanges")
                  | ⚠️ {{ unsavedChangesCount }} selected course(s) have unsaved changes
                
                //- Select all checkbox
                .mb-4.flex.gap-8(v-if="coursesWithAudio.length > 0")
                  label.inline-flex.items-center.gap-2.cursor-pointer
                    input(
                      type="checkbox"
                      :disabled="isCreatingScripts || isCreatingAudio || isPublishing"
                      @change="selectAllCourses"
                      :checked="allCoursesSelected"
                    )
                    span.font-medium Select All
                  
                  label.inline-flex.items-center.gap-2.cursor-pointer(
                    title="Include ALL customisations with a matching base course ID"
                  )
                    input(
                      type="checkbox"
                      v-model="includeDuplicates"
                    )
                    span.font-medium Include Duplicates
                
                .overflow-x-auto.overflow-y-auto.max-h-96(v-if="coursesWithAudio.length > 0")
                  table.w-full.text-sm.m-0.bg-white.dark_bg-gray-800
                    thead
                      tr.border-b.border-gray-300.dark_border-gray-600
                        th.text-center.align-middle.py-2.px-2 Include
                        th.text-center.align-middle.py-2.px-2 Draft Status
                        th.text-center.align-middle.py-2.px-2 Base Course
                        th.text-center.align-middle.py-2.px-2 Firm Code
                        th.text-center.align-middle.py-2.px-2 Course ID
                        th.text-center.align-middle.py-2.px-2 Title
                        th.text-center.align-middle.py-2.px-2 Last Updated
                        th.text-center.align-middle.py-2.px-2 Audio Languages
                        th.text-center.align-middle.py-2.px-2 Script Status
                        th.text-center.align-middle.py-2.px-2 Script Progress
                        th.text-center.align-middle.py-2.px-2 Audio Status
                        th.text-center.align-middle.py-2.px-2 Audio Progress
                        th.text-center.align-middle.py-2.px-2 Publish Status
                    tbody
                      tr.border-b.border-gray-200.dark_border-gray-700(
                        v-for="course in coursesWithAudio"
                        :key="`${course.firmCode}_${course.courseId}`"
                      )
                        td.py-2.px-2.text-center
                          input(
                            type="checkbox"
                            :disabled="isCreatingScripts || isCreatingAudio || isPublishing"
                            @change="toggleCourseSelection(course)"
                            :checked="isCourseSelected(course)"
                          )
                        td.py-2.px-2.text-center
                          .flex.items-center.justify-center.gap-1
                            span.text-sm(v-if="checkingUnsavedChanges")
                              span.text-gray-500 Loading...
                            span.text-sm(v-else-if="getUnsavedChangesStatus(course)?.hasUnsavedChanges")
                              span.text-yellow-600.dark_text-yellow-400.font-bold(:title="getUnsavedChangesTooltip(course)") ⚠️
                            span.text-sm(v-else)
                              span.text-green-600.dark_text-green-400(:title="getUnsavedChangesTooltip(course)") ✓
                            a.text-blue-600.dark_text-blue-400.hover_text-blue-800.dark_hover_text-blue-300(
                              :href="`#/editor/${course.firmCode ? course.firmCode + '_' : ''}${course.courseId}`"
                              title="Edit course"
                              target="_blank"
                            )
                              span.text-sm 📝
                        td.py-2.px-2.font-mono.text-center
                          span(v-if="course.isBaseCourse") {{ course.baseCourseId }} (Base)
                          span(v-else) {{ course.baseCourseId }}
                        td.py-2.px-2.font-mono.text-center.break-words(class="max-w-[10rem]" :title="course.firmCode") {{ course.firmCode }}
                        td.py-2.px-2.font-mono.text-center {{ course.courseId }}
                        td.py-2.px-2.text-center {{ course.title }}
                        td.py-2.px-2.text-center.whitespace-nowrap
                          FormatAgo(:date="Number(course.updatedDate) || 0")
                        td.py-2.px-2.text-center
                          span.text-sm(v-if="formatCourseAudioLanguages(course)") 
                            span(v-if="course.willAddAudio") 
                              span.text-blue-600.dark_text-blue-400 {{ formatCourseAudioLanguages(course) }}
                              span.text-xs.ml-1 (will add)
                            span(v-else-if="course.willAddNewLanguages")
                              span {{ formatCourseAudioLanguages(course) }}
                              span.text-xs.ml-1.text-green-600.dark_text-green-400 (will add new languages)
                            span(v-else) {{ formatCourseAudioLanguages(course) }}
                          span.text-gray-500.text-sm(v-else) None
                        td.py-2.px-2.text-center
                          span.text-sm(v-if="getScriptStatus(course) === 'pending'") 
                            span.text-yellow-600.dark_text-yellow-400 Pending
                          span.text-sm(v-else-if="getScriptStatus(course) === 'success'")
                            span.text-green-600.dark_text-green-400 Complete
                          span.text-sm(v-else-if="getScriptStatus(course) === 'error'")
                            span.text-red-600.dark_text-red-400 Failed
                          span.text-sm(v-else)
                            span.text-gray-500 Not Started
                        td.py-2.px-2.text-center
                          span.text-sm(v-if="getScriptProgress(course)") {{ getScriptProgress(course) }}
                          span.text-sm(v-else) -
                        td.py-2.px-2.text-center
                          span.text-sm(v-if="getAudioCreationOverallStatus(course) === 'pending'") 
                            span.text-yellow-600.dark_text-yellow-400 Pending
                          span.text-sm(v-else-if="getAudioCreationOverallStatus(course) === 'success'")
                            span.text-green-600.dark_text-green-400 Complete
                          span.text-sm(v-else-if="getAudioCreationOverallStatus(course) === 'error'")
                            span.text-red-600.dark_text-red-400 Failed
                          span.text-sm(v-else)
                            span.text-gray-500 Not Started
                        td.py-2.px-2.text-center
                          span.text-sm(v-if="getAudioCreationStatus(course)") {{ getAudioCreationStatus(course) }}
                          span.text-sm(v-else) -
                        td.py-2.px-2.text-center
                          span.text-sm(v-if="getPublishOverallStatus(course) === 'pending'") 
                            span.text-yellow-600.dark_text-yellow-400 Pending
                          span.text-sm(v-else-if="getPublishOverallStatus(course) === 'success'")
                            span.text-green-600.dark_text-green-400 Complete
                          span.text-sm(v-else-if="getPublishOverallStatus(course) === 'error'")
                            span.text-red-600.dark_text-red-400 Failed
                          span.text-sm(v-else-if="getPublishOverallStatus(course) === 'ready'")
                            span.text-blue-600.dark_text-blue-400 Ready
                          span.text-sm(v-else)
                            span.text-gray-500 Not Started
                
                p.text-gray-600.dark_text-gray-400(v-else-if="totalSummary.totalCustomisations > 0")
                  | No customisations with audio found for the specified base course IDs.
                
                p.text-gray-600.dark_text-gray-400(v-else)
                  | No customisations found for the specified base course IDs.
              
          .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg(v-if="bulkAudioMode === 'tier2' && coursesWithAudio.length > 0 && !loadingCustomisations")
            .mb-4.p-3.bg-gray-100.dark_bg-gray-800.rounded-lg
              .flex.items-center.gap-3
                label.inline-flex.items-center.gap-2.cursor-pointer
                  input(
                    type="checkbox"
                    :disabled="isCreatingScripts || isCreatingAudio || isPublishing"
                    v-model="autoContinue"
                  )
                  span.font-medium Auto-continue
                span.text-sm.text-gray-600.dark_text-gray-400
                  | Will automatically proceed to the next step if there are no errors.
            
            .flex.gap-4.flex-col.md_flex-row
              .flex-1.p-4.bg-gray-600.dark_bg-gray-800.bg-opacity-10.rounded-lg.flex.flex-col
                h2.p-0.mt-0 1. Create Audio Scripts
                p.text-sm.mb-4
                  | Create audio scripts for all customisations and Tier-2 (S3) languages (JiT locales excluded)
                p.text-sm.text-purple-700.dark_text-purple-300.mb-2(v-if="selectedCoursesCount > 0 && selectedTier2ScriptsCount === 0")
                  | No Tier-2 audio languages to process for the selected customisations — all configured audio languages use Audio AI JiT.
                
                .flex.flex-col.gap-4.items-center.mt-auto
                  div.w-full.text-center.py-2.px-4.rounded(v-if="!isCreatingScripts && scriptsCompleted")
                    span.text-green-600.dark_text-green-400.text-2xl.font-bold ✓ Success
                  
                  template(v-else)
                    button.w-full.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                      type="button"
                      :disabled="selectedCoursesCount === 0 || selectedTier2ScriptsCount === 0 || isCreatingScripts || hasAnyUnsavedChanges"
                      @click="createAllAudioScripts"
                    )
                      span(v-if="!isCreatingScripts && !hasAnyUnsavedChanges") Create Scripts ({{ selectedTier2ScriptsCount }})
                      span(v-else-if="!isCreatingScripts && hasAnyUnsavedChanges") Unsaved Changes Detected
                      span(v-else) Creating...
                    
                    button.w-full.btn-hover_cursor-pointer.bg-orange-600.btn-hover_bg-orange-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                      type="button"
                      :disabled="failedScripts.length === 0"
                      @click="retryFailedScripts"
                      v-if="failedScripts.length > 0"
                    )
                      span Retry Failed Scripts ({{ failedScripts.length }})
                
                .text-sm.text-gray-600.dark_text-gray-400(v-if="isCreatingScripts")
                  p Creating scripts: {{ completedScripts }}/{{ totalScripts }} completed
                  p(v-if="currentProcessing") Currently processing: {{ currentProcessing }}
  
              .flex-1.p-4.bg-gray-600.dark_bg-gray-800.bg-opacity-10.rounded-lg.flex.flex-col
                h2.p-0.mt-0 2. Generate AI Audio
                p.text-sm.mb-4
                  | Create audio files for all generated Tier-2 scripts (JiT locales excluded)
                
                .flex.flex-col.gap-4.items-center.mt-auto
                  div.w-full.text-center.py-2.px-4.rounded(v-if="!isCreatingAudio && audioCompleted")
                    span.text-green-600.dark_text-green-400.text-2xl.font-bold ✓ Success
                  
                  template(v-else)
                    button.w-full.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                      type="button"
                      :disabled="!allScriptsCreated || isCreatingAudio"
                      @click="startBulkAudioGeneration"
                    )
                      span(v-if="!isCreatingAudio") Generate Audio ({{ readyForAudioCreation.length }})
                      span(v-else) Generating...
                    
                    button.w-full.btn-hover_cursor-pointer.bg-orange-600.btn-hover_bg-orange-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                      type="button"
                      :disabled="failedAudio.length === 0"
                      @click="retryFailedAudio"
                      v-if="failedAudio.length > 0"
                    )
                      span Retry Failed Audio ({{ failedAudio.length }})
  
                .text-sm.text-gray-600.dark_text-gray-400(v-if="isCreatingAudio")
                  p Generating audio: {{ completedAudio }}/{{ totalAudio }} completed
                  p(v-if="currentAudioProcessing") Currently processing: {{ currentAudioProcessing }}
  
              .flex-1.p-4.bg-gray-600.dark_bg-gray-800.bg-opacity-10.rounded-lg.flex.flex-col
                h2.p-0.mt-0 3. Publish Courses
                p.text-sm.mb-4
                  | Publish all customisations.
                
                .flex.flex-col.gap-4.items-center.mt-auto(v-if="!isPublishing && publishingCompleted")
                  div.w-full.text-center.py-2.px-4.rounded
                    span.text-green-600.dark_text-green-400.text-2xl.font-bold ✓ Success
                
                template(v-else)
                  .flex.gap-4.items-center.mb-4
                    span Choose destination:
                    label.inline-flex.gap-2.items-center
                      input(type="radio" v-model="publishDestination" value="test")
                      span Internal
                    label.inline-flex.gap-2.items-center
                      input(type="radio" v-model="publishDestination" value="production")
                      span Live
                  
                  .flex.flex-col.gap-4.items-center.mt-auto
                    button.w-full.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                      type="button"
                      :disabled="!allCoursesReadyForPublishing || isPublishing"
                      @click="startBulkPublishing"
                    )
                      span(v-if="!isPublishing") Publish ({{ readyForPublishing.length }})
                      span(v-else) Publishing...
                    button.w-full.btn-hover_cursor-pointer.bg-orange-600.btn-hover_bg-orange-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                      type="button"
                      :disabled="failedPublishing.length === 0"
                      @click="retryFailedPublishing"
                      v-if="failedPublishing.length > 0"
                    )
                      span Retry Failed Publishing ({{ failedPublishing.length }})
                  
                .text-sm.text-gray-600.dark_text-gray-400(v-if="isPublishing")
                  p Publishing: {{ completedPublishing }}/{{ totalPublishing }} completed
                  p(v-if="currentPublishing") Currently publishing: {{ currentPublishing }}
            
          .flex.flex-col.items-center.justify-center.my-4(v-if="bulkAudioMode === 'tier2'")
            button.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-red-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
              type="button"
              :disabled="!isCreatingScripts && !isCreatingAudio && !isPublishing"
              @click="stopAllProcessing"
              v-if="isCreatingScripts || isCreatingAudio || isPublishing"
            )
              span Stop Processing
</template>

<script setup lang="ts">
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import FormatAgo from './FormatAgo.vue';
import BulkAudioJitCleanup from './BulkAudioJitCleanup.vue';
import useDocTitle from '../lib/useDocTitle';
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { countCustomisations } from '../../lib/callModContent';
import {
  createAIAudio,
  publishCourse,
  getLatestDraft,
  getLatestSaved,
  updateDraft
} from '../lib/callModContentAuthors';
import jsYaml from 'js-yaml';
import languagesMap from '../../lib/languages.json';
import activeLanguages from '../../lib/activeLanguages.yml';
import createAudioScript from '../../lib/createAudioScript.js';
import timestamp from '../../../esm/lib/time-stamp.js';
import {
  extractAudioAiJitConfig,
  getJitEnabledLocalesFromConfig,
  isAudioJitEnabledForLocale,
  type CourseAudioJitConfig
} from '../../lib/audioJitCourseConfig';
import {
  parseHashParams,
  generateHashUrl,
  updateHashUrl,
  getHashParam,
  setHashParam
} from '../lib/hashParams';

declare module '../../lib/activeLanguages.yml' {
  const arr: string[];
  // @ts-ignore
  export default arr;
}

const docTitle = computed(() =>
  bulkAudioMode.value === 'jit-cleanup'
    ? 'Remove Legacy S3 Audio Paths'
    : 'Bulk Audio Creation'
);
const route = useRoute();

const bulkAudioMode = ref<'tier2' | 'jit-cleanup'>('tier2');

const pageTitle = computed(() =>
  bulkAudioMode.value === 'jit-cleanup'
    ? 'Remove Legacy S3 Audio Paths'
    : 'Bulk Audio Generation'
);

useDocTitle(docTitle);

// Language validation setup (similar to BulkCustomisations.vue and AuthorTranslate.vue)
const validLanguages = Object.keys(languagesMap)
  .filter(code => activeLanguages.includes(code))
  .filter(code => !languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

const deprecatedLanguages = Object.keys(languagesMap)
  .filter(code => languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

// Reactive data
const courseIdsInput = ref<string>('');
const firmCodeInput = ref<string>('');
const languageCodesInput = ref<string>('');
const loadingCustomisations = ref(false);
const customisationsReports = ref<Map<number, CustomReport>>(new Map());
const includeDuplicates = ref(true);

// Unsaved changes tracking
const courseUnsavedChanges = ref<
  Map<
    string,
    { hasUnsavedChanges: boolean; draftAuthor?: string; draftDate?: number }
  >
>(new Map());
const checkingUnsavedChanges = ref(false);

// Script creation data
const audioScripts = ref<Map<string, AudioScriptData>>(new Map());
const isCreatingScripts = ref(false);
const currentProcessing = ref('');
const completedScripts = ref(0);
const totalScripts = ref(0);
const concurrencyLimit = 3;
const activeIframes = ref<Set<string>>(new Set());

// Audio creation data
const isCreatingAudio = ref(false);
const currentAudioProcessing = ref('');
const completedAudio = ref(0);
const totalAudio = ref(0);
const audioConcurrencyLimit = 2; // Lower concurrency for audio creation

// Publishing data
const isPublishing = ref(false);
const currentPublishing = ref('');
const completedPublishing = ref(0);
const totalPublishing = ref(0);
const publishConcurrencyLimit = 3;
const publishDestination = ref<'test' | 'production'>('production');

// Course-level publishing status tracking
const coursePublishStatus = ref<
  Map<string, 'idle' | 'ready' | 'pending' | 'success' | 'error'>
>(new Map());
const coursePublishErrors = ref<Map<string, string>>(new Map());

// Stop processing control
const shouldStopProcessing = ref(false);

// Auto-continue option
const autoContinue = ref(true);

// Course selection for processing
const selectedCourses = ref<Set<string>>(new Set());

// Collapse/expand state for the "Add NEW audio languages?" section
const isNewAudioLanguagesExpanded = ref(false);

interface AudioScriptData {
  courseId: number;
  firmCode: string;
  language: string;
  type: string;
  audioScript: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  progress?: string;
  audioCreationStatus?: 'idle' | 'pending' | 'success' | 'error';
  audioCreationError?: string;
  audioPath?: string;
  publishStatus?: 'idle' | 'pending' | 'success' | 'error';
  publishError?: string;
}

// Computed properties
// Helper function to parse language codes input
const parseLanguageCodes = (input: string): string[] => {
  return input
    .split(',')
    .map(code => code.trim())
    .filter(code => code.length > 0);
};

// Language validation computed property
const languageValidationErrors = computed(() => {
  const errors: string[] = [];

  if (
    !languageCodesInput.value ||
    languageCodesInput.value.trim().length === 0
  ) {
    return errors; // Don't show errors for empty field
  }

  // Check for unwanted spaces
  if (languageCodesInput.value !== languageCodesInput.value.trim()) {
    errors.push('Language codes cannot have leading or trailing spaces');
  }

  // Check for spaces in the middle (spaces after comma are optional)
  const parts = languageCodesInput.value.split(',');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i === 0) {
      // First part: no leading spaces allowed
      if (part.startsWith(' ')) {
        errors.push('Language codes cannot have spaces before the first code');
        break;
      }
    } else {
      // Subsequent parts: optional single space after comma, but no multiple spaces
      // Just check for multiple spaces - actual format validation happens against languages.json
      if (
        part.startsWith('  ') ||
        (part.startsWith(' ') && part.trim().length === 0)
      ) {
        errors.push(
          'Only a single space is allowed after commas in language codes'
        );
        break;
      }
    }
  }

  const languages = parseLanguageCodes(languageCodesInput.value);
  if (languages.length === 0) {
    errors.push('At least one language code is required');
  } else {
    // Check for invalid language codes (not in languages.json)
    const invalidLanguages = languages.filter(lang => !languagesMap[lang]);

    if (invalidLanguages.length > 0) {
      errors.push(
        `Invalid language codes: ${invalidLanguages.join(
          ', '
        )} (not found in language list)`
      );
    }

    // Check for deprecated languages
    const deprecatedCodes = languages.filter(
      lang => languagesMap[lang]?.deprecated
    );

    if (deprecatedCodes.length > 0) {
      const deprecatedNames = deprecatedCodes
        .map(code => `${languagesMap[code]?.english || code} (${code})`)
        .join(', ');
      errors.push(
        `Deprecated language codes are not allowed: ${deprecatedNames}. Please use supported language codes.`
      );
    }

    // Check for languages not in active languages list
    const inactiveLanguages = languages.filter(
      lang => languagesMap[lang] && !activeLanguages.includes(lang)
    );

    if (inactiveLanguages.length > 0) {
      errors.push(
        `Inactive language codes: ${inactiveLanguages.join(
          ', '
        )} (not in active languages list)`
      );
    }

    // Check for duplicates
    const uniqueLanguages = [...new Set(languages)];
    if (uniqueLanguages.length !== languages.length) {
      errors.push('Duplicate language codes are not allowed');
    }

    // Check for common invalid patterns (language names instead of codes)
    const commonInvalid = languages.filter(lang =>
      ['english', 'french', 'german', 'spanish', 'italian'].includes(
        lang.toLowerCase()
      )
    );
    if (commonInvalid.length > 0) {
      errors.push(
        `Use language codes, not names: ${commonInvalid.join(
          ', '
        )} (try 'en', 'fr', 'de', 'es', 'it')`
      );
    }
  }

  return errors;
});

// Helper function to extract languages from YAML
const extractLanguagesFromYaml = (yamlContent: string): string[] => {
  try {
    const parsed = jsYaml.load(yamlContent) as any;
    const languages: string[] = [];

    // Check for direct languages property
    if (parsed?.languages && Array.isArray(parsed.languages)) {
      parsed.languages.forEach((lang: string) => {
        if (languagesMap[lang] && !languages.includes(lang)) {
          languages.push(lang);
        }
      });
    }

    // Check for patches with languages
    if (parsed?.patches) {
      Object.keys(parsed.patches).forEach(patchKey => {
        if (patchKey.startsWith('$course.languages.')) {
          const lang = patchKey.replace('$course.languages.', '');
          if (languagesMap[lang] && !languages.includes(lang)) {
            languages.push(lang);
          }
        }
      });
    }

    return languages.sort();
  } catch (error) {
    console.error('Error parsing YAML for languages:', error);
    return [];
  }
};

// Function to populate language codes from selected customisations
const populateLanguageCodesFromCustomisations = () => {
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );

  if (selectedCoursesList.length === 0) {
    alert('Please select at least one customisation first.');
    return;
  }

  const allLanguages = new Set<string>();

  selectedCoursesList.forEach(course => {
    const languages = extractLanguagesFromYaml(course.yaml);
    languages.forEach(lang => allLanguages.add(lang));
  });

  if (allLanguages.size === 0) {
    alert('No languages found in the selected customisations.');
    return;
  }

  // Populate the language codes input
  languageCodesInput.value = Array.from(allLanguages).join(', ');

  console.log(
    `Populated language codes from ${selectedCoursesList.length} customisations:`,
    Array.from(allLanguages)
  );
};

// Helper function to check if course matches firm code filter
const matchesFirmCodeFilter = (course: any): boolean => {
  if (!firmCodeInput.value.trim()) return true; // No filter specified
  return course.firmCode === firmCodeInput.value.trim();
};

// Helper function to check if course should have audio added
const shouldAddAudioToCourse = (course: any): boolean => {
  const hasFirmCodeFilter = firmCodeInput.value.trim().length > 0;
  const hasLanguageCodes = languageCodesInput.value.trim().length > 0;

  if (!hasFirmCodeFilter && !hasLanguageCodes) return false;

  // Check if course matches firm code filter
  if (hasFirmCodeFilter && !matchesFirmCodeFilter(course)) return false;

  // If we have language codes specified, we want to add audio to this course
  return hasLanguageCodes;
};

// Helper function to check if course has audio but not for specified languages
const hasAudioButNotForSpecifiedLanguages = (course: any): boolean => {
  const hasLanguageCodes = languageCodesInput.value.trim().length > 0;
  if (!hasLanguageCodes) return false;

  const specifiedLanguages = parseLanguageCodes(languageCodesInput.value);
  const existingLanguages = getAudioLanguages(course);

  // Check if course has audio but is missing any of the specified languages
  return (
    course.hasAudio &&
    existingLanguages.length > 0 &&
    specifiedLanguages.some(lang => !existingLanguages.includes(lang))
  );
};

const coursesWithAudio = computed(() => {
  const allCourses: any[] = [];
  const seenCourses = new Set<string>();
  const hasFirmCodeFilter = firmCodeInput.value.trim().length > 0;

  customisationsReports.value.forEach((report, baseCourseId) => {
    // Add customisations that have audio
    report.list.forEach(course => {
      const languages = getAudioLanguages(course);
      const courseKey = `${course.firmCode}_${course.courseId}`;

      // If firm code is specified, only include courses that match it
      if (hasFirmCodeFilter && !matchesFirmCodeFilter(course)) {
        return; // Skip this course if it doesn't match the firm code
      }

      if (!includeDuplicates.value && course.courseId !== baseCourseId) {
        return; // Skip this course if it's a duplicate and duplicates are not included
      }

      // Include courses that already have audio
      if (course.hasAudio && languages.length > 0) {
        if (!seenCourses.has(courseKey)) {
          const willAddNewLanguages =
            hasAudioButNotForSpecifiedLanguages(course);
          allCourses.push({
            ...course,
            isBaseCourse: false,
            baseCourseId: baseCourseId,
            willAddAudio: false, // Already has audio
            willAddNewLanguages: willAddNewLanguages // Will add new languages
          });
          seenCourses.add(courseKey);
        }
      }
      // Include courses that should have audio added (no existing audio but matches criteria)
      else if (shouldAddAudioToCourse(course)) {
        if (!seenCourses.has(courseKey)) {
          allCourses.push({
            ...course,
            isBaseCourse: false,
            baseCourseId: baseCourseId,
            willAddAudio: true, // Will add audio
            willAddNewLanguages: false // No existing audio to add to
          });
          seenCourses.add(courseKey);
        }
      }
    });
  });

  // Sort by updatedDate in descending order (most recent first)
  return allCourses.sort((a, b) => {
    const dateA = Number(a.updatedDate) || 0;
    const dateB = Number(b.updatedDate) || 0;
    return dateB - dateA;
  });
});

const totalSummary = computed(() => {
  let totalBaseCourses = 0;
  let totalCustomisations = 0;
  let totalWithAudio = 0;

  customisationsReports.value.forEach((report, baseCourseId) => {
    totalBaseCourses++;
    totalCustomisations += report.total;
    totalWithAudio += report.list.filter(
      course => course.hasAudio && getAudioLanguages(course).length > 0
    ).length;
  });

  return {
    totalBaseCourses,
    totalCustomisations,
    totalWithAudio
  };
});

const failedScripts = computed(() => {
  const failed: AudioScriptData[] = [];
  audioScripts.value.forEach(script => {
    if (script.status === 'error') {
      failed.push(script);
    }
  });
  return failed;
});

const failedAudio = computed(() => {
  const failed: AudioScriptData[] = [];
  audioScripts.value.forEach(script => {
    if (script.audioCreationStatus === 'error') {
      failed.push(script);
    }
  });
  return failed;
});

const readyForAudioCreation = computed(() => {
  const ready: AudioScriptData[] = [];
  audioScripts.value.forEach(script => {
    if (
      script.status === 'success' &&
      script.audioCreationStatus !== 'success'
    ) {
      // Only include scripts for selected courses
      const course = coursesWithAudio.value.find(
        c => c.firmCode === script.firmCode && c.courseId === script.courseId
      );
      if (course && isCourseSelected(course)) {
        ready.push(script);
      }
    }
  });
  return ready;
});

const allScriptsCreated = computed(() => {
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );
  if (selectedCoursesList.length === 0) return false;

  const totalScriptsNeeded = selectedCoursesList.reduce((total, course) => {
    return total + getTier2AudioLanguages(course).length;
  }, 0);

  const successfulScripts = Array.from(audioScripts.value.values()).filter(
    script => script.status === 'success'
  ).length;

  return successfulScripts === totalScriptsNeeded && totalScriptsNeeded > 0;
});

const readyForPublishing = computed(() => {
  const ready: AudioScriptData[] = [];

  coursePublishStatus.value.forEach((status, courseKey) => {
    if (status === 'ready') {
      // Find the first script for this course to use as the representative
      const [firmCode, courseId] = courseKey.split('_');
      const courseScript = Array.from(audioScripts.value.values()).find(
        script =>
          script.firmCode === firmCode && script.courseId === parseInt(courseId)
      );
      if (courseScript) {
        // Only include if the course is selected
        const course = coursesWithAudio.value.find(
          c => c.firmCode === firmCode && c.courseId === parseInt(courseId)
        );
        if (course && isCourseSelected(course)) {
          ready.push(courseScript);
        }
      }
    }
  });

  return ready;
});

const allCoursesReadyForPublishing = computed(() => {
  // Check if all selected courses that have audio are ready for publishing
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );
  if (selectedCoursesList.length === 0) return false;

  // Get unique selected courses (by firmCode_courseId) that have audio
  const uniqueCourses = new Set<string>();
  selectedCoursesList.forEach(course => {
    uniqueCourses.add(`${course.firmCode}_${course.courseId}`);
  });

  // Check if all unique selected courses are ready for publishing
  return Array.from(uniqueCourses).every(courseKey => {
    return coursePublishStatus.value.get(courseKey) === 'ready';
  });
});

const failedPublishing = computed(() => {
  const failed: AudioScriptData[] = [];

  coursePublishStatus.value.forEach((status, courseKey) => {
    if (status === 'error') {
      // Find the first script for this course to use as the representative
      const [firmCode, courseId] = courseKey.split('_');
      const courseScript = Array.from(audioScripts.value.values()).find(
        script =>
          script.firmCode === firmCode && script.courseId === parseInt(courseId)
      );
      if (courseScript) {
        // Only include if the course is selected
        const course = coursesWithAudio.value.find(
          c => c.firmCode === firmCode && c.courseId === parseInt(courseId)
        );
        if (course && isCourseSelected(course)) {
          failed.push(courseScript);
        }
      }
    }
  });

  return failed;
});

// Methods
const parseCourseIds = (input: string): number[] => {
  return input
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0)
    .map(id => parseInt(id))
    .filter(id => !isNaN(id));
};

// Course selection methods
const selectAllCourses = (event: Event) => {
  const { checked } = event.target as HTMLInputElement;
  if (checked) {
    // Select all courses
    coursesWithAudio.value.forEach(course => {
      const courseKey = `${course.firmCode}_${course.courseId}`;
      selectedCourses.value.add(courseKey);
    });
  } else {
    // Deselect all courses
    selectedCourses.value.clear();
  }
};

const toggleCourseSelection = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  if (selectedCourses.value.has(courseKey)) {
    selectedCourses.value.delete(courseKey);
  } else {
    selectedCourses.value.add(courseKey);
  }
};

const isCourseSelected = (course: any): boolean => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return selectedCourses.value.has(courseKey);
};

const checkUnsavedChanges = async (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;

  try {
    const [latestDraft, latestSaved] = await Promise.all([
      getLatestDraft({ courseId: course.courseId, firmCode: course.firmCode }),
      getLatestSaved({ courseId: course.courseId, firmCode: course.firmCode })
    ]);

    const hasUnsavedChanges =
      latestDraft &&
      (latestSaved
        ? latestDraft?.metadata.updatedDate > latestSaved?.metadata.createdDate
        : true);

    courseUnsavedChanges.value.set(courseKey, {
      hasUnsavedChanges: !!hasUnsavedChanges,
      draftAuthor: latestDraft?.author,
      draftDate: latestDraft?.metadata.updatedDate
    });
  } catch (error) {
    console.error(`Error checking unsaved changes for ${courseKey}:`, error);
    courseUnsavedChanges.value.set(courseKey, {
      hasUnsavedChanges: false
    });
  }
};

const checkAllUnsavedChanges = async () => {
  if (coursesWithAudio.value.length === 0) return;

  checkingUnsavedChanges.value = true;
  courseUnsavedChanges.value.clear();

  try {
    // Check unsaved changes for all courses with audio
    await Promise.all(
      coursesWithAudio.value.map(course => checkUnsavedChanges(course))
    );
  } catch (error) {
    console.error('Error checking unsaved changes:', error);
  } finally {
    checkingUnsavedChanges.value = false;
  }
};

const getUnsavedChangesStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return courseUnsavedChanges.value.get(courseKey);
};

const getUnsavedChangesTooltip = (course: any) => {
  const status = getUnsavedChangesStatus(course);
  if (!status?.hasUnsavedChanges) return 'No unsaved changes';

  const draftDate = status.draftDate
    ? new Date(Number(status.draftDate)).toLocaleString()
    : 'Unknown';
  const author = status.draftAuthor || 'Unknown';

  return `Has unsaved changes by ${author} on ${draftDate}`;
};

const hasAnyUnsavedChanges = computed(() => {
  return Array.from(courseUnsavedChanges.value?.entries() || []).some(
    ([courseKey, status]) => {
      // Only check for unsaved changes if the course is selected
      const course = coursesWithAudio.value.find(
        c => `${c.firmCode}_${c.courseId}` === courseKey
      );
      return course && isCourseSelected(course) && status.hasUnsavedChanges;
    }
  );
});

const unsavedChangesCount = computed(() => {
  return Array.from(courseUnsavedChanges.value?.entries() || []).filter(
    ([courseKey, status]) => {
      // Only count unsaved changes if the course is selected
      const course = coursesWithAudio.value.find(
        c => `${c.firmCode}_${c.courseId}` === courseKey
      );
      return course && isCourseSelected(course) && status.hasUnsavedChanges;
    }
  ).length;
});

// Course selection computed properties
const allCoursesSelected = computed(() => {
  if (coursesWithAudio.value.length === 0) return false;
  return coursesWithAudio.value.every(course => {
    const courseKey = `${course.firmCode}_${course.courseId}`;
    return selectedCourses.value.has(courseKey);
  });
});

const selectedCoursesCount = computed(() => {
  return selectedCourses.value.size;
});

const selectedScriptsCount = computed(() => {
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );

  return selectedCoursesList.reduce((total, course) => {
    return total + getAudioLanguages(course).length;
  }, 0);
});

const selectedTier2ScriptsCount = computed(() => {
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );

  return selectedCoursesList.reduce((total, course) => {
    return total + getTier2AudioLanguages(course).length;
  }, 0);
});

// Completion status computed properties
const scriptsCompleted = computed(() => {
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );
  if (selectedCoursesList.length === 0) return false;
  const totalScriptsNeeded = selectedCoursesList.reduce((total, course) => {
    return total + getTier2AudioLanguages(course).length;
  }, 0);
  const successfulScripts = Array.from(audioScripts.value.values()).filter(
    script => script.status === 'success'
  ).length;
  return (
    successfulScripts === totalScriptsNeeded &&
    totalScriptsNeeded > 0 &&
    failedScripts.value.length === 0
  );
});

const audioCompleted = computed(() => {
  // Check if we have any selected courses with audio
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );
  if (selectedCoursesList.length === 0) return false;

  // Count total audio scripts needed for selected courses
  const totalAudioNeeded = selectedCoursesList.reduce((total, course) => {
    return total + getTier2AudioLanguages(course).length;
  }, 0);

  // Count successful audio creation
  const successfulAudio = Array.from(audioScripts.value.values()).filter(
    script => script.audioCreationStatus === 'success'
  ).length;

  // Check if all audio is complete and no failures
  return (
    totalAudioNeeded > 0 &&
    successfulAudio === totalAudioNeeded &&
    failedAudio.value.length === 0
  );
});

const publishingCompleted = computed(() => {
  // Check if we have any selected courses with audio
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );
  if (selectedCoursesList.length === 0) return false;

  // Get unique selected courses (by firmCode_courseId) that have audio
  const uniqueCourses = new Set<string>();
  selectedCoursesList.forEach(course => {
    uniqueCourses.add(`${course.firmCode}_${course.courseId}`);
  });

  const totalPublishingNeeded = uniqueCourses.size;

  // Count successful publishing
  const successfulPublishing = Array.from(
    coursePublishStatus.value.values()
  ).filter(status => status === 'success').length;

  // Count failed publishing
  const failedPublishingCount = Array.from(
    coursePublishStatus.value.values()
  ).filter(status => status === 'error').length;

  // Check if all publishing is complete and no failures
  return (
    totalPublishingNeeded > 0 &&
    successfulPublishing === totalPublishingNeeded &&
    failedPublishingCount === 0
  );
});

const loadCustomisations = async () => {
  const courseIds = parseCourseIds(courseIdsInput.value);
  if (courseIds.length === 0) return;

  resetProcessingState();

  loadingCustomisations.value = true;

  try {
    // Load customisations for each course ID
    for (const courseId of courseIds) {
      try {
        const baseCourse = await getLatestSaved({ courseId });

        if (!baseCourse) {
          throw new Error(`No base course found.`);
        } else if (baseCourse.activityName === 'Conversational Learning') {
          throw new Error(
            `Base course is a Conversational Learning course and cannot be used for bulk customisations.`
          );
        }

        const report = await countCustomisations(courseId);
        customisationsReports.value.set(courseId, report);
      } catch (error) {
        console.error(
          `Error loading customisations for course ${courseId}:`,
          error
        );
        // Continue with other course IDs even if one fails
      }
    }

    // After loading customisations, check for unsaved changes
    await checkAllUnsavedChanges();

    // Initialize all courses as selected by default
    coursesWithAudio.value.forEach(course => {
      const courseKey = `${course.firmCode}_${course.courseId}`;
      selectedCourses.value.add(courseKey);
    });
  } catch (error) {
    console.error('Error loading customisations:', error);
    // You might want to add error handling UI here
  } finally {
    loadingCustomisations.value = false;
  }
};

const formatDate = timestamp => {
  const date = new Date(timestamp);
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  };
  const formatted = date.toLocaleDateString('en-GB', options);
  return formatted;
};

const resetSelection = () => {
  courseIdsInput.value = '';
  firmCodeInput.value = '';
  languageCodesInput.value = '';
  autoContinue.value = true;
  isCreatingScripts.value = false;
  isCreatingAudio.value = false;
  isPublishing.value = false;
  shouldStopProcessing.value = false;
  completedScripts.value = 0;
  totalScripts.value = 0;
  completedAudio.value = 0;
  totalAudio.value = 0;
  completedPublishing.value = 0;
  totalPublishing.value = 0;
  currentProcessing.value = '';
  currentAudioProcessing.value = '';
  currentPublishing.value = '';

  resetProcessingState();

  // Clear URL parameters
  updateHashUrl('/bulk-audio', new URLSearchParams());
};

const toggleNewAudioLanguagesSection = () => {
  if (
    firmCodeInput.value.trim().length > 0 ||
    languageCodesInput.value.trim().length > 0
  ) {
    return;
  }

  isNewAudioLanguagesExpanded.value = !isNewAudioLanguagesExpanded.value;
};

const resetProcessingState = () => {
  // Reset all statuses and clear scripts when starting a new search
  audioScripts.value.clear();
  coursePublishStatus.value.clear();
  coursePublishErrors.value.clear();
  courseUnsavedChanges.value.clear();
  selectedCourses.value.clear();
  isCreatingScripts.value = false;
  isCreatingAudio.value = false;
  isPublishing.value = false;
  loadingCustomisations.value = false;
  shouldStopProcessing.value = false;
  completedScripts.value = 0;
  totalScripts.value = 0;
  completedAudio.value = 0;
  totalAudio.value = 0;
  completedPublishing.value = 0;
  totalPublishing.value = 0;
  currentProcessing.value = '';
  currentAudioProcessing.value = '';
  currentPublishing.value = '';

  // Clean up any active iframes
  activeIframes.value.forEach(scriptKey => {
    const iframe = document.querySelector(
      `iframe[data-script-key="${scriptKey}"]`
    );
    if (iframe) {
      iframe.remove();
    }
  });
  activeIframes.value.clear();
  customisationsReports.value.clear();
};

const getAudioAiJitConfigForCourse = (
  course: any
): CourseAudioJitConfig | undefined => {
  if (!course?.yaml) return undefined;
  try {
    const parsed = jsYaml.load(course.yaml) as Record<string, unknown>;
    return extractAudioAiJitConfig(parsed);
  } catch {
    return undefined;
  }
};

const getJitEnabledLanguages = (course: any): string[] => {
  const config = getAudioAiJitConfigForCourse(course);
  return getJitEnabledLocalesFromConfig(config, getAudioLanguages(course));
};

const getTier2AudioLanguages = (course: any): string[] => {
  const config = getAudioAiJitConfigForCourse(course);
  return getAudioLanguages(course).filter(
    lang => !isAudioJitEnabledForLocale(config, lang)
  );
};

const formatCourseAudioLanguages = (course: any): string => {
  const tier2 = getTier2AudioLanguages(course);
  const jit = getJitEnabledLanguages(course);
  const parts: string[] = [];
  if (tier2.length > 0) parts.push(tier2.join(', '));
  if (jit.length > 0) parts.push(jit.map(lang => `${lang} (JiT)`).join(', '));
  return parts.join('; ');
};

const getAudioLanguages = (course: any): string[] => {
  // If this course will have audio added, return the specified language codes
  if (course.willAddAudio) {
    return parseLanguageCodes(languageCodesInput.value);
  }

  try {
    const parsed = jsYaml.load(course.yaml) as any;
    const audioLanguages: string[] = [];

    // Check for direct audio property
    if (parsed?.course?.audio) {
      Object.keys(parsed?.course?.audio).forEach(lang => {
        if (languagesMap[lang]) {
          audioLanguages.push(lang);
        }
      });
    }

    // Check for patches with audio
    if (parsed?.patches) {
      Object.keys(parsed?.patches).forEach(patchKey => {
        if (patchKey.startsWith('$course.audio.')) {
          const lang = patchKey.replace('$course.audio.', '');
          if (languagesMap[lang] && !audioLanguages.includes(lang)) {
            audioLanguages.push(lang);
          }
        }
      });
    }

    // If this course will have new languages added, include them in the list
    if (course.willAddNewLanguages) {
      const specifiedLanguages = parseLanguageCodes(languageCodesInput.value);
      specifiedLanguages.forEach(lang => {
        if (!audioLanguages.includes(lang)) {
          audioLanguages.push(lang);
        }
      });
    }

    return audioLanguages.sort();
  } catch (error) {
    console.error('Error parsing YAML for audio languages:', error);
    return [];
  }
};

const getScriptStatus = (course: any): string => {
  const languages = getTier2AudioLanguages(course);
  if (languages.length === 0) return 'none';

  const courseKey = `${course.firmCode}_${course.courseId}`;
  const scripts = Array.from(audioScripts.value.values()).filter(
    script =>
      script.firmCode === course.firmCode && script.courseId === course.courseId
  );

  if (scripts.length === 0) return 'none';
  if (scripts.some(s => s.status === 'error')) return 'error';
  if (scripts.some(s => s.status === 'pending')) return 'pending';
  if (scripts.every(s => s.status === 'success')) return 'success';

  return 'pending';
};

const getScriptProgress = (course: any): string => {
  const languages = getTier2AudioLanguages(course);
  if (languages.length === 0) return '';

  const courseKey = `${course.firmCode}_${course.courseId}`;
  const scripts = Array.from(audioScripts.value.values()).filter(
    script =>
      script.firmCode === course.firmCode && script.courseId === course.courseId
  );

  if (scripts.length === 0) return '';

  const completed = scripts.filter(s => s.status === 'success').length;
  const total = languages.length;

  return `${completed}/${total}`;
};

const getAudioCreationStatus = (course: any): string => {
  const languages = getTier2AudioLanguages(course);
  if (languages.length === 0) return '';

  const scripts = Array.from(audioScripts.value.values()).filter(
    script =>
      script.firmCode === course.firmCode && script.courseId === course.courseId
  );

  if (scripts.length === 0) return '';

  const completed = scripts.filter(
    s => s.audioCreationStatus === 'success'
  ).length;
  const total = scripts.filter(s => s.status === 'success').length;

  return total > 0 ? `${completed}/${total}` : '';
};

const getAudioCreationOverallStatus = (course: any): string => {
  const languages = getTier2AudioLanguages(course);
  if (languages.length === 0) return '';

  const scripts = Array.from(audioScripts.value.values()).filter(
    script =>
      script.firmCode === course.firmCode && script.courseId === course.courseId
  );

  if (scripts.length === 0) return '';

  const successfulScripts = scripts.filter(s => s.status === 'success');
  if (successfulScripts.length === 0) return '';

  if (successfulScripts.some(s => s.audioCreationStatus === 'error'))
    return 'error';
  if (successfulScripts.some(s => s.audioCreationStatus === 'pending'))
    return 'pending';
  if (successfulScripts.every(s => s.audioCreationStatus === 'success'))
    return 'success';

  return 'idle';
};

const updateCoursePublishStatus = (course: any) => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  const languages = getTier2AudioLanguages(course);

  if (languages.length === 0) {
    coursePublishStatus.value.set(courseKey, 'idle');
    return;
  }

  const scripts = Array.from(audioScripts.value.values()).filter(
    script =>
      script.firmCode === course.firmCode && script.courseId === course.courseId
  );

  if (scripts.length === 0) {
    coursePublishStatus.value.set(courseKey, 'idle');
    return;
  }

  // Check if all scripts are created and audio is generated
  const allScriptsComplete = scripts.every(s => s.status === 'success');
  const allAudioComplete = scripts.every(
    s => s.audioCreationStatus === 'success'
  );

  if (allScriptsComplete && allAudioComplete) {
    // All ready for publishing
    const currentStatus = coursePublishStatus.value.get(courseKey);
    if (
      currentStatus !== 'pending' &&
      currentStatus !== 'success' &&
      currentStatus !== 'error'
    ) {
      coursePublishStatus.value.set(courseKey, 'ready');
    }
  } else {
    // Not ready for publishing
    coursePublishStatus.value.set(courseKey, 'idle');
  }
};

const getPublishOverallStatus = (course: any): string => {
  const courseKey = `${course.firmCode}_${course.courseId}`;
  return coursePublishStatus.value.get(courseKey) || 'idle';
};

const createAllAudioScripts = async () => {
  const selectedCoursesList = coursesWithAudio.value.filter(course =>
    isCourseSelected(course)
  );

  if (selectedCoursesList.length === 0) return;

  isCreatingScripts.value = true;
  shouldStopProcessing.value = false;
  completedScripts.value = 0;
  totalScripts.value = 0;
  audioScripts.value.clear();

  // Calculate total scripts needed for selected courses only
  selectedCoursesList.forEach(course => {
    const languages = getTier2AudioLanguages(course);
    totalScripts.value += languages.length;
  });

  // Create script entries for selected courses and languages only
  selectedCoursesList.forEach(course => {
    const languages = getTier2AudioLanguages(course);
    languages.forEach(language => {
      const scriptKey = `${course.firmCode}_${course.courseId}_${language}`;
      audioScripts.value.set(scriptKey, {
        courseId: course.courseId,
        firmCode: course.firmCode,
        type: course.type || 'custom',
        language,
        audioScript: '',
        status: 'pending'
      });
    });

    // Initialize course publish status
    const courseKey = `${course.firmCode}_${course.courseId}`;
    coursePublishStatus.value.set(courseKey, 'idle');
  });

  // Start processing with concurrency limit
  await processScriptsWithConcurrency();
};

const processScriptsWithConcurrency = async () => {
  const pendingScripts = Array.from(audioScripts.value.entries()).filter(
    ([_, script]) => script.status === 'pending'
  );

  if (pendingScripts.length === 0 || shouldStopProcessing.value) {
    isCreatingScripts.value = false;
    currentProcessing.value = '';
    shouldStopProcessing.value = false;

    // Auto-continue to audio generation if enabled and no errors
    if (
      autoContinue.value &&
      failedScripts.value.length === 0 &&
      readyForAudioCreation.value.length > 0
    ) {
      setTimeout(() => {
        startBulkAudioGeneration();
      }, 500);
    }
    return;
  }

  const batch = pendingScripts.slice(0, concurrencyLimit);

  await Promise.all(
    batch.map(([scriptKey, script]) =>
      createSingleAudioScript(scriptKey, script)
    )
  );

  // Continue with next batch if not stopped
  if (!shouldStopProcessing.value) {
    setTimeout(processScriptsWithConcurrency, 100);
  } else {
    isCreatingScripts.value = false;
    currentProcessing.value = '';
    shouldStopProcessing.value = false;
  }
};

const createSingleAudioScript = async (
  scriptKey: string,
  script: AudioScriptData
) => {
  try {
    if (shouldStopProcessing.value) return;

    const course = coursesWithAudio.value.find(
      c => c.firmCode === script.firmCode && c.courseId === script.courseId
    );
    if (
      course &&
      isAudioJitEnabledForLocale(
        getAudioAiJitConfigForCourse(course),
        script.language
      )
    ) {
      return;
    }

    currentProcessing.value = `${script.firmCode}_${script.courseId} (${script.language})`;

    const { iframe, pages } = await createAndLoadIframe(script);
    const audioScript = await createAudioScript(pages);

    if (shouldStopProcessing.value) {
      iframe.remove();
      activeIframes.value.delete(scriptKey);
      return;
    }

    // Update script data
    script.audioScript = audioScript;
    script.status = 'success';
    script.progress = 'Complete';
    audioScripts.value.set(scriptKey, script);

    completedScripts.value++;

    if (course) {
      updateCoursePublishStatus(course);
    }

    // Clean up iframe
    iframe.remove();
    activeIframes.value.delete(scriptKey);
  } catch (error) {
    console.error(`Error creating script for ${scriptKey}:`, error);

    script.status = 'error';
    script.error = error.message || 'Unknown error';
    script.progress = 'Failed';
    audioScripts.value.set(scriptKey, script);

    completedScripts.value++;
    activeIframes.value.delete(scriptKey);
  }
};

const createAndLoadIframe = async (
  script: AudioScriptData
): Promise<{ iframe: HTMLIFrameElement; pages: Element }> => {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    const scriptKey = `${script.firmCode}_${script.courseId}_${script.language}`;
    iframe.setAttribute('data-script-key', scriptKey);
    document.body.appendChild(iframe);

    const courseQ = `courseid=${script.courseId}`;
    const firmQ = script.firmCode ? `firm=${script.firmCode}` : '';
    const langQ = `lang=${script.language}`;
    const tagQ = 'tags=worddoc+includeall&audioDoc=1&ucAuthorAudio=1';
    const queryString = ['turbo=999999', courseQ, firmQ, langQ, tagQ].join('&');
    const localServer = window.location.origin.includes('local');
    const portalOrigin = new URL(window.location.href).origin;

    const url = localServer
      ? `./app.html?${queryString}`
      : `${portalOrigin}/latest/app.html?${queryString}`;

    iframe.src = url;

    const checkIframe = () => {
      try {
        const cw = iframe.contentWindow as Window;
        const pages = cw.document.documentElement.querySelector('.pages');
        if (pages) {
          resolve({ iframe, pages });
        } else {
          setTimeout(checkIframe, 500);
        }
      } catch (error) {
        setTimeout(checkIframe, 500);
      }
    };

    iframe.addEventListener('load', () => {
      const cw = iframe.contentWindow as Window;
      cw.onerror = (message, source, lineno, colno, error) => {
        reject(
          new Error(
            `Error in iframe: ${message} at ${source}:${lineno}:${colno}`
          )
        );
      };

      if (cw.document.readyState === 'complete') {
        checkIframe();
      } else {
        cw.document.onreadystatechange = () => {
          if (cw.document.readyState === 'complete') {
            checkIframe();
          }
        };
      }
    });

    iframe.addEventListener('error', () => {
      reject(new Error('Failed to load iframe'));
    });

    // Timeout after 30 seconds
    setTimeout(() => {
      reject(new Error('Iframe load timeout'));
    }, 30000);
  });
};

const retryFailedScripts = async () => {
  const failedScriptsList = failedScripts.value;

  // Reset failed scripts to pending
  failedScriptsList.forEach(script => {
    const scriptKey = `${script.firmCode}_${script.courseId}_${script.language}`;
    script.status = 'pending';
    script.error = undefined;
    script.progress = undefined;
    audioScripts.value.set(scriptKey, script);
  });

  // Restart processing
  isCreatingScripts.value = true;
  await processScriptsWithConcurrency();
};

const pollAudioStatus = (
  summaryFilePath: string,
  interval = 5000
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const checkStatus = async () => {
      try {
        const summaryData = await fetch(summaryFilePath, {
          cache: 'no-cache'
        }).then(res => res.json());
        console.log('Checking audio status:', summaryData.status);
        if (summaryData.status === 'success') {
          // remove the filename to keep just the path
          const pathOnly = summaryFilePath.substring(
            0,
            summaryFilePath.lastIndexOf('/') + 1
          );
          resolve(pathOnly);
        } else if (summaryData.status === 'error') {
          reject(new Error('Audio creation failed'));
        } else {
          setTimeout(checkStatus, interval);
        }
      } catch (error) {
        reject(error);
      }
    };
    checkStatus();
  });
};

const generateSingleAudio = async (
  scriptKey: string,
  script: AudioScriptData
) => {
  try {
    if (shouldStopProcessing.value) return;

    const course = coursesWithAudio.value.find(
      c => c.firmCode === script.firmCode && c.courseId === script.courseId
    );
    if (
      course &&
      isAudioJitEnabledForLocale(
        getAudioAiJitConfigForCourse(course),
        script.language
      )
    ) {
      return;
    }

    currentAudioProcessing.value = `${script.firmCode}_${script.courseId} (${script.language})`;

    // Initialize audio creation status
    script.audioCreationStatus = 'pending';
    script.audioCreationError = undefined;
    audioScripts.value.set(scriptKey, script);

    const data = {
      courseId: script.courseId,
      firmCode: script.firmCode,
      lang: script.language,
      audioScript: script.audioScript,
      voiceData: '', // Use default voice for bulk processing
      uploadAudio: true
    };

    console.log('Generating audio for:', scriptKey, data);

    const response = await createAIAudio(data);

    if (shouldStopProcessing.value) return;

    if (!response?.summary) {
      throw new Error('No summary path returned from audio generation');
    }

    // Poll for completion
    const audioPath = await pollAudioStatus(response.summary);

    if (shouldStopProcessing.value) return;

    // Update script data
    script.audioCreationStatus = 'success';
    script.audioPath = audioPath;
    script.audioCreationError = undefined;
    audioScripts.value.set(scriptKey, script);

    completedAudio.value++;

    if (course) {
      updateCoursePublishStatus(course);
    }
  } catch (error) {
    console.error(`Error generating audio for ${scriptKey}:`, error);

    script.audioCreationStatus = 'error';
    script.audioCreationError = error.message || 'Unknown error';
    audioScripts.value.set(scriptKey, script);

    completedAudio.value++;
  }
};

const processAudioWithConcurrency = async () => {
  const pendingAudio = Array.from(audioScripts.value.entries()).filter(
    ([_, script]) =>
      script.status === 'success' && script.audioCreationStatus === 'pending'
  );

  if (pendingAudio.length === 0 || shouldStopProcessing.value) {
    isCreatingAudio.value = false;
    currentAudioProcessing.value = '';
    shouldStopProcessing.value = false;

    // Auto-continue to publishing if enabled and no errors
    if (
      autoContinue.value &&
      failedAudio.value.length === 0 &&
      readyForPublishing.value.length > 0
    ) {
      setTimeout(() => {
        startBulkPublishing();
      }, 500);
    }
    return;
  }

  const batch = pendingAudio.slice(0, audioConcurrencyLimit);

  await Promise.all(
    batch.map(([scriptKey, script]) => generateSingleAudio(scriptKey, script))
  );

  // Continue with next batch if not stopped
  if (!shouldStopProcessing.value) {
    setTimeout(processAudioWithConcurrency, 100);
  } else {
    isCreatingAudio.value = false;
    currentAudioProcessing.value = '';
    shouldStopProcessing.value = false;
  }
};

const startBulkAudioGeneration = async () => {
  if (readyForAudioCreation.value.length === 0) return;

  isCreatingAudio.value = true;
  shouldStopProcessing.value = false;
  completedAudio.value = 0;
  totalAudio.value = readyForAudioCreation.value.length;

  // Set all ready scripts to pending
  readyForAudioCreation.value.forEach(script => {
    const scriptKey = `${script.firmCode}_${script.courseId}_${script.language}`;
    script.audioCreationStatus = 'pending';
    script.audioCreationError = undefined;
    audioScripts.value.set(scriptKey, script);
  });

  // Start processing with concurrency limit
  await processAudioWithConcurrency();
};

const retryFailedAudio = async () => {
  const failedAudioList = failedAudio.value;

  // Reset failed audio to pending
  failedAudioList.forEach(script => {
    const scriptKey = `${script.firmCode}_${script.courseId}_${script.language}`;
    script.audioCreationStatus = 'pending';
    script.audioCreationError = undefined;
    audioScripts.value.set(scriptKey, script);
  });

  // Restart processing
  isCreatingAudio.value = true;
  shouldStopProcessing.value = false;
  await processAudioWithConcurrency();
};

const publishSingleCourse = async (
  scriptKey: string,
  script: AudioScriptData
) => {
  try {
    if (shouldStopProcessing.value) return;

    const courseKey = `${script.firmCode}_${script.courseId}`;
    currentPublishing.value = courseKey;

    // Set course publish status to pending
    coursePublishStatus.value.set(courseKey, 'pending');
    coursePublishErrors.value.delete(courseKey);

    // Create draft object for publishing
    const draft = {
      courseId: script.courseId,
      firmCode: script.firmCode,
      type: script.type || 'custom',
      ubercourseVersion: 'latest'
    };

    // Publish with "small" settings to hide from clients
    const publishData = {
      level: 'minor',
      message: '',
      internal: 'Bulk audio update'
    };

    console.log(
      'Publishing course:',
      courseKey,
      draft,
      publishDestination.value
    );

    if (shouldStopProcessing.value) return;

    // Use the same logic as PublishDialog
    const publish =
      publishDestination.value === 'production' ? publishLive : publishInternal;
    await publish(draft, publishData);

    if (shouldStopProcessing.value) return;

    // Update course publish status to success
    coursePublishStatus.value.set(courseKey, 'success');
    coursePublishErrors.value.delete(courseKey);

    completedPublishing.value++;
  } catch (error) {
    console.error(`Error publishing course ${scriptKey}:`, error);

    // Update course publish status to error
    const courseKey = `${script.firmCode}_${script.courseId}`;
    coursePublishStatus.value.set(courseKey, 'error');
    coursePublishErrors.value.set(courseKey, error.message || 'Unknown error');

    completedPublishing.value++;
  }
};

const publishLive = (draft: any, publishData: any) =>
  Promise.all([
    publishCourse({
      draft,
      publishData,
      publishTo: 'production'
    }),
    publishCourse({
      draft,
      publishData,
      publishTo: 'test'
    })
  ]);

const publishInternal = (draft: any, publishData: any) =>
  publishCourse({
    draft,
    publishData,
    publishTo: 'test'
  });

const processPublishingWithConcurrency = async () => {
  const pendingPublishing = Array.from(
    coursePublishStatus.value.entries()
  ).filter(([_, status]) => status === 'pending');

  if (pendingPublishing.length === 0 || shouldStopProcessing.value) {
    isPublishing.value = false;
    currentPublishing.value = '';
    shouldStopProcessing.value = false;
    return;
  }

  const batch = pendingPublishing.slice(0, publishConcurrencyLimit);

  await Promise.all(
    batch.map(([courseKey, _]) => {
      const [firmCode, courseId] = courseKey.split('_');
      const courseScript = Array.from(audioScripts.value.values()).find(
        script =>
          script.firmCode === firmCode && script.courseId === parseInt(courseId)
      );
      if (courseScript) {
        const scriptKey = `${firmCode}_${courseId}_${courseScript.language}`;
        return publishSingleCourse(scriptKey, courseScript);
      }
    })
  );

  // Continue with next batch if not stopped
  if (!shouldStopProcessing.value) {
    setTimeout(processPublishingWithConcurrency, 100);
  } else {
    isPublishing.value = false;
    currentPublishing.value = '';
    shouldStopProcessing.value = false;
  }
};

const startBulkPublishing = async () => {
  if (readyForPublishing.value.length === 0) return;

  isPublishing.value = true;
  shouldStopProcessing.value = false;
  completedPublishing.value = 0;
  totalPublishing.value = readyForPublishing.value.length;

  // Set all ready courses to pending
  readyForPublishing.value.forEach(script => {
    const courseKey = `${script.firmCode}_${script.courseId}`;
    coursePublishStatus.value.set(courseKey, 'pending');
    coursePublishErrors.value.delete(courseKey);
  });

  // Start processing with concurrency limit
  await processPublishingWithConcurrency();
};

const retryFailedPublishing = async () => {
  const failedPublishingList = failedPublishing.value;
  const processedCourses = new Set<string>();

  // Reset failed publishing to pending (one per course)
  failedPublishingList.forEach(script => {
    const courseKey = `${script.firmCode}_${script.courseId}`;

    // Only process each course once
    if (!processedCourses.has(courseKey)) {
      coursePublishStatus.value.set(courseKey, 'pending');
      coursePublishErrors.value.delete(courseKey);
      processedCourses.add(courseKey);
    }
  });

  // Restart processing
  isPublishing.value = true;
  shouldStopProcessing.value = false;
  await processPublishingWithConcurrency();
};

const stopAllProcessing = () => {
  shouldStopProcessing.value = true;

  // Clean up any active iframes
  activeIframes.value.forEach(scriptKey => {
    const iframe = document.querySelector(
      `iframe[data-script-key="${scriptKey}"]`
    );
    if (iframe) {
      iframe.remove();
    }
  });
  activeIframes.value.clear();

  console.log('Stopping all processing...');
};

// Configuration object for customisations report
const reportConfig = {
  includeCustomisations: true,
  includeAudioOnly: true,
  includeCustomLogo: true,
  includeCustomTitle: true,
  includeCustomContent: true,
  includePatchedScreens: true,
  includeLanguages: true,
  includeAudioLanguages: true,
  includeLastUpdated: true,
  includeDraftStatus: true
};

// Function to analyze course YAML and extract relevant information
const analyzeCourseYaml = (yamlContent: string, course: any) => {
  try {
    const parsed = jsYaml.load(yamlContent) as any;
    const analysis: any = {
      courseId: course.courseId,
      firmCode: course.firmCode,
      title: course.title,
      baseCourseId: course.baseCourseId,
      lastUpdated: course.updatedDate,
      hasAudio: course.hasAudio
    };

    // Languages
    if (reportConfig.includeLanguages) {
      const languages: string[] = [];
      if (parsed?.languages) {
        parsed?.languages?.forEach((lang: string) => {
          if (languagesMap[lang]) {
            languages.push(lang);
          }
        });
      }

      // do we ever uses patches for the languages?
      if (parsed.patches) {
        Object.keys(parsed.patches).forEach(patchKey => {
          if (patchKey.startsWith('$course.languages.')) {
            const lang = patchKey.replace('$course.languages.', '');
            if (languagesMap[lang] && !languages.includes(lang)) {
              languages.push(lang);
            }
          }
        });
      }
      analysis.languages = languages.join(', ');
      analysis.languageCount = languages.length;
    }

    // Audio languages
    if (reportConfig.includeAudioLanguages) {
      const audioLanguages: string[] = [];
      if (parsed?.course?.audio) {
        Object.keys(parsed.course.audio).forEach(lang => {
          if (languagesMap[lang]) {
            audioLanguages.push(lang);
          }
        });
      }
      if (parsed.patches) {
        Object.keys(parsed.patches).forEach(patchKey => {
          if (patchKey.startsWith('$course.audio.')) {
            const lang = patchKey.replace('$course.audio.', '');
            if (languagesMap[lang] && !audioLanguages.includes(lang)) {
              audioLanguages.push(lang);
            }
          }
        });
      }
      analysis.audioLanguages = audioLanguages.join(', ');
      analysis.jitLanguages = getJitEnabledLocalesFromConfig(
        extractAudioAiJitConfig(parsed),
        audioLanguages
      ).join(', ');
    }

    // Custom title
    if (reportConfig.includeCustomTitle) {
      analysis.hasCustomTitle = !!(
        parsed.course?.title ||
        (parsed.patches &&
          Object.keys(parsed.patches).some(key => key === '$course.title'))
      );
    }

    // Custom logo
    if (reportConfig.includeCustomLogo) {
      analysis.hasCustomLogo = !!(
        parsed.course?.logo ||
        (parsed.patches &&
          Object.keys(parsed.patches).some(key => key === '$course.logo'))
      );
    }

    // Full customisation (has pages)
    if (reportConfig.includeCustomContent) {
      analysis.hasFullCustomisation = !!(
        parsed.pages &&
        Array.isArray(parsed.pages) &&
        parsed.pages.length > 0
      );
    }

    // Patched screens analysis
    if (reportConfig.includePatchedScreens) {
      const patchedScreens: string[] = [];
      const shorthandPatches: string[] = [];

      if (parsed.patches) {
        Object.keys(parsed.patches).forEach(patchKey => {
          // Check if it's a shorthand patch (starts with $ or #)
          if (patchKey.startsWith('$') || patchKey.startsWith('#')) {
            shorthandPatches.push(patchKey);
          } else {
            // Check if it's a screen patch by looking for the patch structure
            const patchValue = parsed.patches[patchKey];
            if (
              Array.isArray(patchValue) &&
              patchValue.length > 0 &&
              patchValue[0] &&
              typeof patchValue[0] === 'object' &&
              patchValue[0].id
            ) {
              patchedScreens.push(patchKey);
            }
          }
        });
      }

      analysis.patchedScreens = patchedScreens.join(', ');
      analysis.shorthandPatches = shorthandPatches.join(', ');
      analysis.patchedScreensCount = patchedScreens.length;
      analysis.shorthandPatchesCount = shorthandPatches.length;
    }

    // Audio-only detection
    if (reportConfig.includeAudioOnly) {
      const hasAudio = analysis.hasAudio || false;
      const hasFullCustomisation = analysis.hasFullCustomisation || false;
      const hasPatchedScreens = (analysis.patchedScreensCount || 0) > 0;
      const hasCustomLogo = analysis.hasCustomLogo || false;
      const hasCustomTitle = analysis.hasCustomTitle || false;

      // Audio-only if it has audio but no full customisation, no patched screens, and minimal other customisations
      analysis.isAudioOnly =
        hasAudio &&
        !hasFullCustomisation &&
        !hasPatchedScreens &&
        ((!hasCustomLogo && !hasCustomTitle) ||
          hasCustomLogo ||
          hasCustomTitle);
    }

    // Draft status
    if (reportConfig.includeDraftStatus) {
      const unsavedStatus = getUnsavedChangesStatus(course);
      analysis.hasUnsavedChanges = unsavedStatus?.hasUnsavedChanges || false;
      analysis.draftAuthor = unsavedStatus?.draftAuthor || '';
      analysis.draftDate = unsavedStatus?.draftDate
        ? new Date(Number(unsavedStatus.draftDate)).toLocaleString()
        : '';
    }

    return analysis;
  } catch (error) {
    console.error('Error analyzing course YAML:', error);
    return {
      courseId: course.courseId,
      firmCode: course.firmCode,
      title: course.title,
      error: 'Failed to parse YAML'
    };
  }
};

// Function to convert analysis data to TSV and trigger download
const downloadCustomisationsReport = () => {
  if (customisationsReports.value.size === 0) return;

  const dts = timestamp('YYYY-MM-DD-HHmmss');
  const allCourses: any[] = [];

  // Collect all customisations
  customisationsReports.value.forEach((report, baseCourseId) => {
    // Add customisations
    if (reportConfig.includeCustomisations) {
      report.list.forEach(course => {
        allCourses.push({
          ...course,
          baseCourseId: baseCourseId
        });
      });
    }
  });

  // Analyze each course
  const analyzedCourses = allCourses.map(course =>
    analyzeCourseYaml(course.yaml, course)
  );

  // Define TSV headers based on configuration
  const headers = ['Base Course ID', 'Firm Code', 'Course ID', 'Title'];

  if (reportConfig.includeLanguages) {
    headers.push('Languages', 'Language Count');
  }
  if (reportConfig.includeAudioLanguages) {
    headers.push('Audio Languages', 'JiT Languages', 'Has Audio');
  }
  if (reportConfig.includeCustomTitle) {
    headers.push('Has Custom Title');
  }
  if (reportConfig.includeCustomLogo) {
    headers.push('Has Custom Logo');
  }
  if (reportConfig.includeCustomContent) {
    headers.push('Has Full Customisation');
  }
  if (reportConfig.includePatchedScreens) {
    headers.push(
      'Patched Screens',
      'Patched Screens Count',
      'Shorthand Patches',
      'Shorthand Patches Count'
    );
  }
  if (reportConfig.includeAudioOnly) {
    headers.push('Is Audio Only');
  }
  if (reportConfig.includeLastUpdated) {
    headers.push('Last Updated');
  }
  if (reportConfig.includeDraftStatus) {
    headers.push('Has Unsaved Changes', 'Draft Author', 'Draft Date');
  }

  // Create TSV rows
  const tsvRows = [headers.join('\t')];

  analyzedCourses.forEach(course => {
    const row = [
      course.baseCourseId,
      course.firmCode,
      course.courseId,
      course.title
    ];

    if (reportConfig.includeLanguages) {
      row.push(course.languages || '', course.languageCount || 0);
    }
    // if we have detected an audio prop in the yaml but no audio languages, then it's an old format with audio somewhere in the yaml (not course level)
    if (reportConfig.includeAudioLanguages) {
      row.push(
        course.audioLanguages || '',
        course.jitLanguages || '',
        course.hasAudio && course.audioLanguages
          ? 'Yes'
          : course.hasAudio
            ? 'Yes (old format?)'
            : 'No'
      );
    }
    if (reportConfig.includeCustomTitle) {
      row.push(course.hasCustomTitle ? 'Yes' : 'No');
    }
    if (reportConfig.includeCustomLogo) {
      row.push(course.hasCustomLogo ? 'Yes' : 'No');
    }
    if (reportConfig.includeCustomContent) {
      row.push(course.hasFullCustomisation ? 'Yes' : 'No');
    }
    if (reportConfig.includePatchedScreens) {
      row.push(
        course.patchedScreens || '',
        course.patchedScreensCount || 0,
        course.shorthandPatches || '',
        course.shorthandPatchesCount || 0
      );
    }
    if (reportConfig.includeAudioOnly) {
      row.push(course.isAudioOnly ? 'Yes' : 'No');
    }
    if (reportConfig.includeLastUpdated) {
      row.push(
        course.lastUpdated
          ? new Date(Number(course.lastUpdated)).toLocaleString()
          : ''
      );
    }
    if (reportConfig.includeDraftStatus) {
      row.push(
        course.hasUnsavedChanges ? 'Yes' : 'No',
        course.draftAuthor || '',
        course.draftDate || ''
      );
    }

    tsvRows.push(row.join('\t'));
  });

  // Create and download the file
  const tsvContent = tsvRows.join('\n');
  const blob = new Blob([tsvContent], { type: 'text/tab-separated-values' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `customisations-report-[${allCourses.length}-results]-${dts}.tsv`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  a.remove();
};

// URL parameter parsing using hash-based parameters
const parseUrlParameters = () => {
  const urlParams = parseHashParams();

  // Parse course IDs from URL
  const courseIdsParam =
    urlParams.get('courseIds') ||
    urlParams.get('courseids') ||
    urlParams.get('courses');
  if (courseIdsParam) {
    courseIdsInput.value = courseIdsParam;
  }

  // Parse firm code from URL
  const firmCodeParam =
    urlParams.get('firmCode') ||
    urlParams.get('firmcode') ||
    urlParams.get('firm');
  if (firmCodeParam) {
    firmCodeInput.value = firmCodeParam;
    includeDuplicates.value = false;
    isNewAudioLanguagesExpanded.value = true;
  }

  // Parse languages from URL
  const languagesParam =
    urlParams.get('languages') ||
    urlParams.get('langs') ||
    urlParams.get('lang');
  if (languagesParam) {
    languageCodesInput.value = languagesParam;
    includeDuplicates.value = false;
    isNewAudioLanguagesExpanded.value = true;
  }

  // autoLoad param
  const autoLoadParam = urlParams.get('autoLoad') === 'true';

  // Auto-load if all parameters are provided
  const hasAllParams = courseIdsParam && firmCodeParam && languagesParam;
  if (hasAllParams && autoLoadParam) {
    // Small delay to ensure reactive updates are processed
    setTimeout(() => {
      loadCustomisations();
    }, 100);
  }
};

// Debounced URL parameter parsing to avoid multiple calls
let parseTimeout: NodeJS.Timeout | null = null;
const debouncedParseUrlParameters = () => {
  if (parseTimeout) {
    clearTimeout(parseTimeout);
  }
  parseTimeout = setTimeout(() => {
    if (route.path === '/bulk-audio') {
      parseUrlParameters();
    }
  }, 50);
};

// Watch for route changes and re-parse parameters
watch(
  () => route.path,
  () => {
    if (route.path === '/bulk-audio') {
      debouncedParseUrlParameters();
    }
  },
  { immediate: true }
);

// Watch for hash changes (including query parameters)
watch(
  () => route.fullPath,
  () => {
    if (route.path === '/bulk-audio') {
      debouncedParseUrlParameters();
    }
  },
  { immediate: true }
);

// Listen for browser navigation events
let handleHashChange: (() => void) | null = null;

onMounted(() => {
  handleHashChange = () => {
    if (route.path === '/bulk-audio') {
      debouncedParseUrlParameters();
    }
  };

  window.addEventListener('popstate', handleHashChange);
  window.addEventListener('hashchange', handleHashChange);
});

onUnmounted(() => {
  if (parseTimeout) {
    clearTimeout(parseTimeout);
  }
  if (handleHashChange) {
    window.removeEventListener('popstate', handleHashChange);
    window.removeEventListener('hashchange', handleHashChange);
  }
});
</script>

<style scoped></style>
